import React, { useEffect, useState } from "react";
import { Event, Location } from "interfaces/events";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, Empty, Table } from "antd";
import dayjs from "dayjs";
import { getMainResponsible } from "helpers/functions";

interface Props {
  companyId: number;
}

const AllCompanyEvents: React.FC<Props> = ({ companyId }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<{ [key: number]: string }>({});
  const [responsibleMembers, setResponsibleMembers] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    axios
      .get<Event[]>(`/companies/${companyId}/events/`)
      .then(response => {
        setEvents(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    const fetchLocation = async (locationId: number) => {
      if (!locationId) return "Ikke bestemt";
      try {
        const response = await axios.get<Location>(`/events/locations/${locationId}/`);
        return response.data.name;
      } catch {
        return "Ukjent";
      }
    };

    const fetchResponsible = async (eventId: number) => {
      try {
        const mainResponsible = await getMainResponsible(eventId);
        return mainResponsible?.full_name ?? "Ukjent";
      } catch {
        return "Ukjent";
      }
    };

    const loadLocations = async () => {
      const uniqueLocationIds = Array.from(new Set(events.map(event => event.location)));
      const locationPromises = uniqueLocationIds.map(locationId =>
        fetchLocation(locationId).then(name => [locationId, name] as [number, string])
      );
      const locationEntries = await Promise.all(locationPromises);
      setLocations(Object.fromEntries(locationEntries));
    };

    const loadResponsibles = async () => {
      const responsiblePromises = events.map(event =>
        fetchResponsible(event.id).then(responsible => [event.id, responsible] as [number, string])
      );
      const responsibleEntries = await Promise.all(responsiblePromises);
      setResponsibleMembers(Object.fromEntries(responsibleEntries));
    };

    loadLocations();
    loadResponsibles();
  }, [events]);

  const eventColumns = [
    {
      title: "Visningsnavn",
      dataIndex: "name",
      ellipsis: true,
      render: (name: string, record: Event) => {
        return <Link to={`/admin/events/${record.id}`}>{name}</Link>;
      },
    },
    {
      title: "Ansvarlig",
      ellipsis: true,
      dataIndex: "id",
      render: (eventId: number) => responsibleMembers[eventId] ?? "Ukjent",
    },
    {
      title: "Tidspunkt",
      dataIndex: "start",
      render: (start: string) => dayjs(start).format("DD.MM.YYYY - HH:mm"),
      ellipsis: true,
    },
    {
      title: "Sted",
      dataIndex: "location",
      ellipsis: true,
      render: (locationId: number) => locations[locationId],
    },
    {
      title: "Påmeldte",
      dataIndex: "registrations",
      ellipsis: true,
      render: (registrations: number, record: unknown) => {
        const event = record as Event;
        return dayjs().isBefore(dayjs(event.open)) ? "Ikke åpnet" : `${registrations}/${event.seats}`;
      },
    },
  ];

  return (
    <>
      <Card title="Arrangementer">
        <Table
          columns={eventColumns}
          dataSource={events.sort((a, b) => dayjs(b.start).diff(dayjs(a.start)))}
          size="small"
          rowKey="id"
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ingen arrangementer" />,
          }}
          loading={loading}
          pagination={{ pageSize: 5 }}
        />
      </Card>
    </>
  );
};

export default AllCompanyEvents;
